/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


$(document).on('click', '.product__action button', function (e) {
  e.preventDefault();
  var dataId = $(this).parents('.form__body').find('.swatch-element label.current').attr('data-id');
  var obj = {
    id: dataId,
    quantity: 1
  }
  addToCart(obj);
})

function addToCart(obj) {
  $.ajax({
    url: '/cart/add.js',
    type: 'POST',
    data: obj,
    dataType: 'json',
    success: function (data) {
      document.querySelector('cart-form').refresh();
      setTimeout(function () {
        $('.cart-link')[0].click();
      }, 1000)
      getCart();
    },
    error: function (err) {
      console.log('error', err);
    }
  })
}

function getCart() {
  $.ajax({
    url: '/cart.js',
    type: 'GET',
    dataType: 'json',
    success: function (cart) {

      $('.cart-link__count').html(cart.item_count);

    },
    error: function (err) {
      console.log('error', err);
    }
  })
}

/** Faq accordion ***/
window.dynamicFreeShippingMessageUpdate = async () => {
  handleShippingMessage = (
    initialShippingMessage,
    shippingThreshold,
    currencyText
  ) => {
    const cartTotalPrice = parseInt(document.querySelector(".site-header").getAttribute("data-cart-total-price")) / 100;

    const awayShippingMsg = document.querySelectorAll(
      ".free-shipping-away-message"
    );
    const finalShippingMsg = document.querySelectorAll(
      ".free-shipping-success"
    );

    if (cartTotalPrice === 0) {
      Array.from(awayShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(finalShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(initialShippingMessage).forEach(item => {
        item.style.display = "block";
      });
    } else if (cartTotalPrice > 0) {
      if (cartTotalPrice >= parseInt(shippingThreshold)) {
        Array.from(awayShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "block";
        });
      } else {
        let awayShipping = (
          parseInt(shippingThreshold) - cartTotalPrice
        ).toFixed(2);
        let awayShippingCurrency = `USD`;
        let cookieCurrency = 'USD';
        if (cookieCurrency === "CAD") {
          awayShipping = Currency.convert(
            awayShipping,
            "USD",
            "CAD"
          ).toFixed(2);
          awayShippingCurrency = "CAD";
        }
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(awayShippingMsg).forEach(item => {
          item.querySelector(
            ".away-money"
          ).innerHTML = `$${awayShipping} ${awayShippingCurrency}`;
          item.style.display = "block";
        });

      }
    }

    if (currencyText) {
      Array.from(initialShippingMessage).forEach(item => {
        const moneyElement = item.querySelector(".money");
        if (moneyElement) {
          moneyElement.innerText = moneyElement.innerText.replace("USD", "");
        }
      });

      Array.from(awayShippingMsg).forEach(item => {
        const awayMoneyElement = item.querySelector(".away-money");
        if (awayMoneyElement) {
          awayMoneyElement.innerText = awayMoneyElement.innerText.replace("USD", " ");
        }
      });
    }
  };
  freeShippingMessageUpdate();
}

if ($('.product-carousel').length) {
  $(".product-carousel").slick({
    dots: false,
    infinite: true,
    slidesToShow: 6,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 3
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2
        }
      }
    ]
  });
}


// swatch code start here
$(function () {
  var body = $("body").attr("data-template");
  if (body == "product") {
    $(".swatch :radio").click(function () {
      var optionIndex = $(this).closest(".swatch").attr("data-option-index");
      var optionValue = $(this).val();
      var selected_availability = $(this).attr("data-available");
      $("#AddToCart").parent().removeClass("disabled");
      $(".flex-wrap li").each(function () {
        var popup_value = $(this).data("value");
        if (popup_value == optionValue) {
          $(".flex-wrap label").removeClass("current");
          $(this).find("label").addClass("current");
        }
      });

      // Update product images as per selected variant
      var $slider_main = $(".slider-content"),
        $slider_thumbnail_li = $(".slider-thumb .gallery__item");
      var selected_size = $(this).attr("data-id");
      $slider_thumbnail_li.each(function () {
        var slide_id = $(this).attr("data-id");
        if (selected_size === slide_id) {
          var slide_active = $(this).attr("data-slick-index");
          $slider_main.slick("slickGoTo", slide_active);
        }
      });

      $(".swatch label").removeClass("current");
      $(this).parent().find("label").addClass("current");

      $(this)
        .closest("form")
        .find(".single-option-selector")
        .eq(optionIndex)
        .val(optionValue)
        .trigger("change");
    });

    // Start -- fix selecting multiple variants per product on product.outfit page
  } else if (body.indexOf("product.outfit") != -1) {
    $(".swatch").each(function () {
      var pid = $(this).attr("data-pid");

      /* start -- Disable add to cart button when no product variant is available */
      let swatchInput = this.querySelectorAll("input");
      let isProductAvailable = [];
      for (i = 0; i < swatchInput.length; i++) {
        let value_avail = swatchInput[i].getAttribute("data-available");
        isProductAvailable.push(value_avail);
      }

      let productAvailablecheck = valueIn => {
        return valueIn == "false";
      };

      if (isProductAvailable.every(productAvailablecheck) === true) {
        $("#AddToCart-" + pid)
          .parent()
          .addClass("disabled");
        $("#AddToCart-" + pid).text("Out of stock");
        $("#AddToCart-" + pid)
          .parent()
          .addClass("disabled__click");
        // $('#AddToCart-'+pid).prop('disabled',true);
      }
      /* end -- Disable add to cart button when no product variant is available */

      $(".swatch-" + pid + " :radio").click(function () {
        var optionIndex = $(this).closest(".swatch").attr("data-option-index");
        var optionValue = $(this).val();
        $("#AddToCart-" + pid).prop("disabled", false);
        $("#AddToCart-" + pid).text("Add to cart");

        $(".button_confirm button").prop("disabled", true);
        $(".button_confirm button").css({ opacity: 0.5 });

        var selected_availability = $(this).attr("data-available");
        $("#AddToCart-" + pid)
          .parent()
          .removeClass("disabled");
        $("#AddToCart-" + pid)
          .parent()
          .removeClass("disabled__click");

        $(this).parent().parent().find("label").removeClass("current");
        $(this).parent().find("label").addClass("current");

        var vid = $(this).attr("data-id");
        $("#productSelect-" + pid + " option").removeAttr("selected");
        $("#productSelect-" + pid + " option[value='" + vid + "']").attr(
          "selected",
          "selected"
        );

        if (selected_availability == "false") {
          $("#AddToCart-" + pid)
            .parent()
            .addClass("disabled");
          $("#AddToCart-" + pid).text("Out of stock");
          $("#AddToCart-" + pid)
            .parent()
            .addClass("disabled__click");
        }
      });
    });
  }
  // End -- fix selecting multiple variants per product on product.outfit page
  // Start -- Fix Gift card button post selector
  else if (body == "product.gift_card") {
    $(".swatch :radio").click(function () {
      var optionIndex = $(this).closest(".swatch").attr("data-option-index");
      var optionValue = $(this).val();
      var selected_availability = $(this).attr("data-available");
      $("#AddToCart").parent().removeClass("disabled");
      $(".flex-wrap div").each(function () {
        var popup_value = $(this).data("value");

        if (popup_value == optionValue) {
          $(".flex-wrap label").removeClass("current");
          $(this).find("label").addClass("current");

          $(this)
            .closest("form")
            .find(".single-option-selector")
            .eq(optionIndex)
            .val(optionValue)
            .trigger("change");
        }
      });
    });
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const productForms = document.querySelectorAll(".product-form");
  productForms.forEach(attachSwatchEventListener);

  function attachSwatchEventListener(formItem) {
    formItem.addEventListener("click", evt => {
      if (
        evt.target.classList.contains("swatch-element") ||
        evt.target.parentElement.classList.contains("swatch-element")
      ) {
        let noVariantSelectElement = evt.currentTarget.querySelector(
          ".js-no-variant-selected"
        );
        if (noVariantSelectElement !== null)
          noVariantSelectElement.classList.remove("js-no-variant-selected");
      }
    });
  }
});

$(document).on('click', '.template-outfit .outfit-submit', function (e) {
  e.preventDefault();
  var id = $(this).parents('#AddToCartForm').find('select[name="id"] option:selected').val();
  var obj = {
    id: id,
    quantity: 1
  }
  addToCart(obj);
});
function addToCart(obj) {
  $.ajax({
    url: '/cart/add.js',
    type: 'POST',
    data: obj,
    dataType: 'json',
    success: function (data) {

      const cartForm = document.querySelector('cart-form');
      if (cartForm && cartForm.enableAjaxUpdate) {
        cartForm.refresh();
      }
      setTimeout(function () {
        $('.cart-link')[0].click();
      }, 700)
      getCartIcon();
      setTimeout(function () {
        window.dynamicFreeShippingMessageUpdate();
      }, 1200)
    },
    error: function (err) {
      console.log('error', err);
    }
  })
}
function getCartIcon() {
  $.ajax({
    url: '/cart.js',
    type: 'GET',
    dataType: 'json',
    success: function (cart) {
      document.querySelector(".site-header").setAttribute("data-cart-total-price", cart.items_subtotal_price)
      $('.cart-link__count').html(cart.item_count);
    },
    error: function (err) {
      console.log('error', err);
    }
  })
}

window.dynamicFreeShippingMessageUpdate = async () => {
  handleShippingMessage = (
    initialShippingMessage,
    shippingThreshold,
    currencyText
  ) => {
    const cartTotalPrice = parseInt(document.querySelector(".site-header").getAttribute("data-cart-total-price")) / 100;

    const awayShippingMsg = document.querySelectorAll(
      ".free-shipping-away-message"
    );
    const finalShippingMsg = document.querySelectorAll(
      ".free-shipping-success"
    );

    if (cartTotalPrice === 0) {
      Array.from(awayShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(finalShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(initialShippingMessage).forEach(item => {
        item.style.display = "block";
      });
    } else if (cartTotalPrice > 0) {
      if (cartTotalPrice >= parseInt(shippingThreshold)) {
        Array.from(awayShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "block";
        });
      } else {
        let awayShipping = (
          parseInt(shippingThreshold) - cartTotalPrice
        ).toFixed(2);
        let awayShippingCurrency = `USD`;
        let cookieCurrency = 'USD';
        if (cookieCurrency === "CAD") {
          awayShipping = Currency.convert(
            awayShipping,
            "USD",
            "CAD"
          ).toFixed(2);
          awayShippingCurrency = "CAD";
        }
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(awayShippingMsg).forEach(item => {
          item.querySelector(
            ".away-money"
          ).innerHTML = `$${awayShipping} ${awayShippingCurrency}`;
          item.style.display = "block";
        });
      }
    }

    if (currencyText) {
      Array.from(initialShippingMessage).forEach(item => {
        const moneyElement = item.querySelector(".money");
        if (moneyElement) {
          moneyElement.innerText = moneyElement.innerText.replace("USD", "");
        }
      });

      Array.from(awayShippingMsg).forEach(item => {
        const awayMoneyElement = item.querySelector(".away-money");
        if (awayMoneyElement) {
          awayMoneyElement.innerText = awayMoneyElement.innerText.replace("USD", " ");
        }
      });
    }
  };
  freeShippingMessageUpdate();
}

//  document.addEventListener('DOMContentLoaded', function() {
//   const backButton = document.querySelector('.navigation__tier-3-back');

//   backButton.addEventListener('click', function() {
//       const tier2Parent = document.querySelector('.navigation__tier-2 .navigation__item.navigation__item--with-children').parentNode;
//       tier2Parent.classList.remove('navigation__item--open');
//   });
// });

document.addEventListener("DOMContentLoaded", function () {

  // Navigation 3 tier open close
  var backButtons = document.querySelectorAll(".navigation__tier-3-back");
  backButtons.forEach(function (button) {
    button.addEventListener("click", function (event) {
      var parentItem = event.target.closest(".navigation__tier-2 li.navigation__item--open");
      if (parentItem) {
        parentItem.classList.remove("navigation__item--open");
      }
    });
  });

  function checkWindowSize() {
    var windowWidth = window.innerWidth;
    if (windowWidth < 767) {
      var mainSearch = document.querySelector(".main-search");
      var mobileHeader = document.querySelector(".navigation__mobile-header");
      mobileHeader.appendChild(mainSearch);
      var mobileNavDrawer = document.querySelector(".mobile-navigation-drawer");
      var lastChild = mobileNavDrawer.lastElementChild;
      mobileNavDrawer.removeChild(lastChild);
    }
  }
  checkWindowSize();
});

$(document).ready(function () {
  var $gallery = $(".media-gallery--layout-carousel-beside");
  var $main = $gallery.find(".main__image-slider");
  var $thumbs = $gallery.find(".thumbnails__slider");

  // Mobile gets tap-to-play instead of autoplay: the video starts, with sound,
  // only when its play button is tapped, and a tap on the video stops it again.
  // Everything from 769px up keeps autoplaying as before.
  function isMobile() {
    return window.matchMedia("(max-width: 768px)").matches;
  }

  // Whether the shopper has interacted with the page yet. Browsers only grant
  // audio once they have, so until then a video plays muted.
  var pageTouched = false;

  // The video the gallery wants playing right now. Tracked here rather than read
  // back from slick, which only moves its .slick-current class once the fade has
  // finished - long after the slide change has to start the video.
  var wantedVideo = null;

  function galleryVideos() {
    return $gallery.find("video");
  }

  function currentSlideVideo() {
    return $main.find(".slick-slide.slick-current").find("video").get(0);
  }

  function startPlaying(video) {
    try {
      var playing = video.play();
      if (playing && typeof playing.catch === "function") {
        playing.catch(function () { });
      }
    } catch (e) { }
  }

  function pauseAllVideos(except) {
    galleryVideos().each(function () {
      if (except && this === except) return;
      this.dataset.tapPlay = "";
      try { this.pause(); } catch (e) { }
    });
  }

  /**
   * Whether a video is the shopper's to start: its media has a play button and
   * we are on mobile. These must never be played by anything but a tap.
   */
  function isTapToPlayMedia(media) {
    if (!media || !isMobile()) return false;

    // Either the mobile-only button an autoplaying video gets, or the poster's
    // own play button, which is what a video with autoplay off waits for. Both
    // are the shopper's to press, and both need their tap handled here: on iOS
    // the sound is granted to the play made inside the tap, and to no other.
    return !!media.querySelector(".js-play-video") || isClickToPlayMedia(media);
  }

  function isTapToPlay(video) {
    return isTapToPlayMedia(video.closest(".product-media--video"));
  }

  /**
   * Whether a media is waiting behind its poster for the shopper's play button.
   *
   * With 'Autoplay videos' off the gallery keeps that button over every video,
   * on desktop as well as mobile, and only a press on it may start playback -
   * so a slide change must neither unpack the deferred media nor play it. On
   * mobile the press itself is handled by handleGalleryTap, the same way an
   * autoplaying video's is; on desktop video.js listens on the button.
   */
  function isClickToPlayMedia(media) {
    return !!media && media.dataset.autoplay === "false";
  }

  function isClickToPlay(video) {
    return isClickToPlayMedia(video.closest(".product-media--video"));
  }

  /**
   * Plays a gallery video, with sound if the browser will allow it.
   *
   * Autoplay is only ever granted to a muted element, so the video is started
   * muted and the mute lifted afterwards - never the other way round, which
   * would leave a still frame whenever audio is refused.
   */
  function playSlideVideo(video) {
    if (!video) return;

    // Left for the shopper to start - the slide change isn't their play button.
    if (isClickToPlay(video)) return;

    wantedVideo = video;
    video.playsInline = true;
    if (video.paused || video.ended) {
      video.muted = true;
      startPlaying(video);
    }
    if (!pageTouched) return;

    video.muted = false;

    // Safari answers a refused unmute by pausing the element rather than by
    // rejecting play(), so the promise proves nothing - check what actually
    // happened. One pause isn't proof either: the theme's own gallery code
    // pauses every video mid-interaction, so try again before calling it a
    // refusal.
    setTimeout(function () {
      if (video === wantedVideo && video.paused) startPlaying(video);
    }, 300);

    setTimeout(function () {
      if (video !== wantedVideo || !video.paused) return;
      // Give up on the sound and keep the video playing silently. Remembered,
      // because retrying on every scroll would only stutter it.
      video.dataset.soundRefused = "true";
      video.muted = true;
      startPlaying(video);
    }, 700);
  }

  /**
   * Tells Video.js the shopper is still here.
   *
   * Video.js hides its control bar after a few seconds of what it believes is
   * inactivity, and it works that out from mouse and touch events we have taken
   * off it (see handleGalleryTap). Left alone the bar would fade out mid-tap and
   * stop answering at all. Video.js hangs the player off the video element it
   * adopts, which is the only handle on it here - and there is none on desktop,
   * or before its autoSetup has run, hence the guard.
   */
  function reportActivityToVideoJs(video) {
    var player = video && video.player;
    if (!player || typeof player.reportUserActivity !== "function") return;
    try { player.reportUserActivity(); } catch (e) { }
  }

  /**
   * Starts a video the shopper has just tapped. The tap is itself the gesture
   * the browser needs before it will allow the sound.
   */
  function playOnTap(video) {
    if (!video) return;

    video.playsInline = true;
    video.dataset.tapPlay = "true";
    // A video left on its last frame starts over rather than going nowhere.
    if (video.ended) video.currentTime = 0;
    video.muted = false;
    startPlaying(video);
  }

  function handleSlideMedia() {
    // Nothing plays by itself on mobile, and nothing may be paused here either:
    // setPosition fires on every window resize, which iOS Safari triggers just
    // by scrolling as the URL bar collapses.
    if (isMobile()) return;

    var videoEl = currentSlideVideo();

    // Pause the other videos, but leave the current one alone - stopping and
    // replaying it on each resize would strip its sound back off.
    pauseAllVideos(videoEl);
    wantedVideo = videoEl || null;
    if (!videoEl) return;

    // Already running, but silently - something else started it, most likely the
    // theme's own gallery code, which plays the active video without a word
    // about its sound. Worth lifting the mute, unless we have been told no.
    var silentlyPlaying = !videoEl.paused && videoEl.muted
      && videoEl.dataset.soundRefused !== "true";

    if (videoEl.paused || videoEl.ended || silentlyPlaying) playSlideVideo(videoEl);
  }

  if ($gallery.length) {
    var galleryEl = $gallery.get(0);

    // A swipe or a scroll that started on the video must not be read as a tap
    // on it, or dragging to the next slide would stop the video on the way.
    var touchStart = null;
    var touchMoved = false;

    galleryEl.addEventListener("touchstart", function (evt) {
      touchStart = evt.touches[0] || null;
      touchMoved = false;
    }, { passive: true });

    galleryEl.addEventListener("touchmove", function (evt) {
      var touch = evt.touches[0];
      if (!touch || !touchStart) return;
      if (Math.abs(touch.clientX - touchStart.clientX) > 10
        || Math.abs(touch.clientY - touchStart.clientY) > 10) touchMoved = true;
    }, { passive: true });

    // Bound in the capture phase so the flag is set before slick's own handlers
    // run the slide change that wants to unmute a video.
    ["pointerdown", "touchstart", "keydown"].forEach(function (type) {
      document.addEventListener(type, function () {
        pageTouched = true;
      }, { capture: true, passive: true });
    });

    // 'play' and 'pause' don't bubble, so these listen in the capture phase.
    galleryEl.addEventListener("play", function (evt) {
      var video = evt.target;

      // The theme's own gallery code plays the active video whenever the media
      // selection changes, and it knows nothing about tap-to-play, so undo any
      // playback the shopper didn't ask for.
      if (isTapToPlay(video) && video.dataset.tapPlay !== "true") {
        video.pause();
        return;
      }

      var media = video.closest(".product-media--video");
      if (media) media.classList.add("is-video-playing");
    }, true);

    galleryEl.addEventListener("pause", function (evt) {
      var media = evt.target.closest(".product-media--video");
      if (media) media.classList.remove("is-video-playing");
    }, true);

    // When this tap was last acted on. One tap can arrive here twice - as the
    // touch and again as the click the browser synthesises from it - and the
    // second is not the shopper asking for the opposite of the first.
    var lastTapAt = 0;

    // Taps on the play button and on the video itself. Bound in the capture
    // phase because slick stops click propagation on its own list whenever it
    // has just seen a swipe, and a tap on the video must never be lost that way.
    function handleGalleryTap(evt) {
      var media = evt.target.closest(".product-media--video");
      if (!isTapToPlayMedia(media) || touchMoved) return;

      // Video.js's control bar is the one thing here that owns its own taps.
      // Swallowing them would leave its play, mute, seek and fullscreen buttons
      // dead, so get out of the way - and count reaching for that bar as the
      // shopper asking for playback, so the play listener above lets it stand.
      if (evt.target.closest(".vjs-control-bar")) {
        var barVideo = media.querySelector("video");
        if (barVideo) barVideo.dataset.tapPlay = "true";
        return;
      }

      // Nothing else gets a say in this tap - not slick, and not Video.js's
      // click-to-toggle on the video, which would answer the tap that pauses
      // the video by playing it straight back. Both events are stopped, the
      // repeat one below included, so neither can reach it.
      evt.preventDefault();
      evt.stopPropagation();

      var now = Date.now();
      if (now - lastTapAt < 400) return;
      lastTapAt = now;

      // The slide may still be holding its <template>, if the shopper got here
      // before the deferred media loaded.
      var video = media.querySelector("video");
      if (!video && typeof media.loadContent === "function") {
        media.loadContent(false, false, "click");
        video = media.querySelector("video");
      }
      if (!video) return;

      // Anywhere on the video - the play button included - starts it, and a
      // second tap stops it. The whole media is the target, so a tap can't be
      // lost to the gap between the video and the frame around it.
      if (video.paused) {
        playOnTap(video);
        // Gets the poster out of the way where it is the play button's backing
        // (autoplay off - see custom.css), without waiting to be told the video
        // is running. It goes back up when the video ends.
        media.classList.add("is-video-started");
      } else if (!evt.target.closest(".js-play-video, .js-load-media")) {
        video.dataset.tapPlay = "";
        video.pause();
      }

      reportActivityToVideoJs(video);
    }

    // touchend as well as click, and on iOS it is the one that counts: Video.js
    // puts its own touchend listener on the video and calls preventDefault
    // there, so the browser never synthesises the click. Taking the touch first
    // - and stopping it before it reaches Video.js - is what keeps a tap on the
    // video able to pause it.
    galleryEl.addEventListener("touchend", handleGalleryTap, true);
    galleryEl.addEventListener("click", handleGalleryTap, true);
  }

  function adjustThumbnailHeight() {
    var mainSliderHeight =
      $main.find(".slick-list").outerHeight() ||
      $main.find(".slick-slide.slick-current").outerHeight();
    if (mainSliderHeight) {
      $thumbs.css("height", mainSliderHeight);
    }
  }

  // Bind events before initializing slick
  $main.on("init reInit setPosition", function () {
    adjustThumbnailHeight();
    handleSlideMedia();
  });

  $main.on("beforeChange", function (event, slick, currentSlide, nextSlide) {
    // On mobile the incoming video waits for its play button - all this has to
    // do is stop the outgoing one.
    if (isMobile()) {
      wantedVideo = null;
      pauseAllVideos();
      return;
    }

    var $next = $main.find('.slick-slide[data-slick-index="' + nextSlide + '"]');

    // Start the incoming video here, not in afterChange. beforeChange still runs
    // inside the tap or swipe that triggered the slide change, whereas
    // afterChange only fires once the fade has finished.
    //
    // On templates that don't autoplay video, the incoming slide holds nothing
    // but a <template> until something asks deferred-media to unpack it. Do that
    // here, while the tap or swipe is still live, and declare it a click: that
    // is the branch of video.js which starts a video with its sound already on,
    // because the interaction that loaded it is the permission it needs.
    var deferred = $next.find("deferred-media").get(0);
    if (deferred && !isClickToPlayMedia(deferred) && !deferred.hasAttribute("loaded")
      && typeof deferred.loadContent === "function") {
      deferred.loadContent(false, false, "click");
    }

    var nextVideo = $next.find("video").get(0);

    // Pause the outgoing videos, but never the one we are about to play.
    pauseAllVideos(nextVideo);
    wantedVideo = nextVideo || null;
    playSlideVideo(nextVideo);
  });

  $main.on("afterChange", function () {
    // After slide change, handle the new slide media
    adjustThumbnailHeight();
    handleSlideMedia();
  });

  // Initialize main slider
  $main.slick({
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    arrows: false,
    asNavFor: ".thumbnails__slider",
    responsive: [
      {
        breakpoint: 991,
        settings: {
          dots: true,
          arrows: true,
          infinite: false,
          adaptiveHeight: true
        }
      }
    ]
  });

  // Initialize thumbnails slider
  $thumbs.slick({
    dots: false,
    infinite: false,
    slidesToShow: 6.5,
    slidesToScroll: 1,
    vertical: true,
    verticalSwiping: true,
    asNavFor: ".main__image-slider",
    focusOnSelect: true,
    responsive: [
      {
        breakpoint: 1399,
        settings: {
          slidesToShow: 4.5,
          slidesToScroll: 1
        }
      }
    ]
  });

  // Adjust thumbnails height on load and resize
  $(window).on("load resize", adjustThumbnailHeight);
});

window.recommendationsLoaded = function () {
  $(".related-collection-product").slick({
    dots: false,
    infinite: false,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 899,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3
        }
      },
      {
        breakpoint: 567,
        settings: {
          slidesToShow: 1.35,
          slidesToScroll: 1
        }
      }
    ]
  });
};


// swatch element js

var body = $("body").attr("data-template");
$(document).on('click', '.swatch :radio', function () {
  var pid = $(this).closest('.swatch').attr("data-pid");
  $(".swatch-element").find("label").removeClass("current");
  var optionIndex = $(this).closest('.swatch').attr('data-option-index');
  var optionValue = $(this).val();
  $('#AddToCart-' + pid).prop('disabled', false);
  $('#AddToCart-' + pid).text("Add to cart");

  $(".button_confirm button").prop("disabled", true);
  $(".button_confirm button").css({ "opacity": 0.5 });

  var selected_availability = $(this).attr('data-available');
  $('#AddToCart-' + pid).parent().removeClass("disabled");
  $('#AddToCart-' + pid).parent().removeClass("disabled__click");

  $(this).parent().parent().find("label").removeClass("current");
  $(this).parent().find("label").addClass("current");

  var vid = $(this).attr("data-id");
  var vvalue = $(this).attr('value');
  $('body').attr('data-swatchvalue', vvalue);

  $("#productSelect-" + pid + " option").removeAttr("selected");
  $("#productSelect-" + pid + " option[value='" + vid + "']").attr("selected", "selected");
  $('.product-block__details-' + pid).find('input[name="id"]').val(vid);

  if (selected_availability == "false") {
    $('#AddToCart-' + pid).parent().addClass("disabled");
    $('#AddToCart-' + pid).text("Out of stock");
    $('#AddToCart-' + pid).parent().addClass("disabled__click");
  }

  var preorder_state = $(this).attr('data-preorder-state');
  if (selected_availability == "true" && preorder_state == 'ON_PREORDER') {
    $('#AddToCart-' + pid).text("Pre-order");
  }

  // Remove slide up classes from all other product items
  let addToCartButtons = document.querySelectorAll('.cust-product__action button');
  let slideUpDivs = document.querySelectorAll('.product-block__detail');
  let productActionDivs = document.querySelectorAll('.cust-product__action');

  for (let i = 0; i < addToCartButtons.length; i++) {
    addToCartButtons[i].classList.remove('cust-slide-up__add-to-cart');
    slideUpDivs[i].classList.remove('cust-slide-wrap__move');
    productActionDivs[i].classList.remove('custom-visibility');
  }

  // Add slide selector classes to product item
  $('.product-block__details-' + pid).addClass("cust-slide-wrap__move");
  $('#AddToCart-' + pid).addClass("cust-slide-up__add-to-cart");
  $('.cust-product__action-' + pid).addClass("custom-visibility");

  // Fetch price data directly from input
  var formattedPrice = $(this).data('formatted-price');
  var formattedCompareAtPrice = $(this).data('formatted-compare-at-price');
  var discountedPrice = $(this).data('discounted-price');
  var hasSiteDiscount = $(this).data('has-site-discount');

  // DOM blocks for this product
  var $detailsBlock = $('.product-block__details-' + pid);
  var $priceBlock = $detailsBlock.find('.product__price');
  var $wasBlock = $detailsBlock.find('.price__was');
  var $headInner = $detailsBlock.find('.product__head-inner.price');

  // Price UI logic
  if (discountedPrice) { // Discounted: show sale price and strikethrough original price
    $priceBlock.html(discountedPrice);
    $wasBlock.html(formattedPrice).show();
  } else if (formattedCompareAtPrice && formattedCompareAtPrice !== formattedPrice) { // Compare at ONLY when really on sale
    $priceBlock.html(formattedPrice);
    $wasBlock.html(formattedCompareAtPrice).show();
  } else { // Regular price
    $priceBlock.html(formattedPrice);
    $wasBlock.hide();
  }
  if (hasSiteDiscount === true || hasSiteDiscount === "true") {
    $headInner.addClass('has-site-discount');
  } else {
    $headInner.removeClass('has-site-discount');
  }
});


document.addEventListener('click', function (event) {
  var isClickInsideSwatch = false;
  var target = event.target;

  while (target) {
    if (target.classList.contains('swatch-element')) {
      isClickInsideSwatch = true;
      break;
    }
    else if (target.classList.contains('cust-product__action--button')) {
      isClickInsideSwatch = true;
    }
    target = target.parentElement;
  }

  if (!isClickInsideSwatch) {
    var parent = event.target.parentElement;
    while (parent) {
      var productAction = parent.querySelector('.product__action');
      var productDetail = parent.querySelector('.product-block__detail');

      if (productAction) {
        if (productAction.classList.contains('custom-visibility')) {
          if (productAction) productAction.classList.remove('custom-visibility');
          if (productDetail) productDetail.classList.remove('cust-slide-wrap__move');
        }
        break;
      }
      var activeSwatch = parent.querySelectorAll('.swatch-element label');
      activeSwatch.forEach(function (label) {
        label.classList.remove('current');
      });
      parent = parent.parentElement;
    }
  }
});

// swatch element js end

// attentive waitlist trigger js start
(() => {
  /**
   * Decides whether the waitlist trigger should be visible.
   * @param {object|undefined} variant - Selected variant, or undefined when the shopper has made
   *   no selection or only a partial one (e.g. Color chosen but Size not).
   * @param {object} eligibility - Map of variant id to waitlist eligibility.
   * @param {boolean} productHasAvailableVariant - Whether any variant is still purchasable.
   * @returns {boolean} true to show the trigger, false to hide it.
   */
  const shouldShowWaitlistTrigger = (variant, eligibility, productHasAvailableVariant) => {
    if (!variant) return !productHasAvailableVariant;
    return eligibility[variant.id] === true;
  };

  document.addEventListener('on:variant:change', (event) => {
    const section = event.target.closest('.js-product');
    const trigger = section && section.querySelector('#attn-waitlist-trigger');
    if (!trigger) return;

    const eligibilityEl = document.getElementById('attn-waitlist-eligibility');
    if (!eligibilityEl) return;

    let eligibility;
    try {
      eligibility = JSON.parse(eligibilityEl.textContent);
    } catch (e) {
      return;
    }

    trigger.hidden = !shouldShowWaitlistTrigger(
      event.detail.variant,
      eligibility,
      trigger.dataset.productAvailable === 'true'
    );
  });
})();
// attentive waitlist trigger js end